// The Spark inbox: newest first; drag a spark onto a board to place it there (it leaves Spark).
import { useState, type FormEvent } from 'react';
import { sparkArray } from '../../shared/doc.js';
import { moveSpark } from '../../shared/ops.js';
import { CARD_DRAG_TYPE } from '../canvas/Canvas.js';
import { CardBody } from '../cards/CardBody.js';
import { createFromText, createImages, inSpark, type Toast } from '../cards/create.js';
import type { ProjectConn } from '../doc/useProject.js';
import { useYVersion } from '../doc/useY.js';

export function SparkAdd({ conn, projectId, toast }: { conn: ProjectConn; projectId: string; toast: Toast }) {
  const [text, setText] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    createFromText(conn, projectId, inSpark, text);
    setText('');
  };
  return (
    <form className="spark-add" onSubmit={submit}>
      <textarea aria-label="New spark" placeholder="An idea, a link…" value={text} onChange={(e) => setText(e.target.value)} rows={2} />
      <div className="row">
        <button type="submit" disabled={!text.trim()}>Add</button>
        <label className="button">
          📷 Photo
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            data-testid="spark-photo"
            onChange={(e) => {
              void createImages(conn, projectId, [...(e.target.files ?? [])], () => inSpark, toast);
              e.target.value = '';
            }}
          />
        </label>
      </div>
    </form>
  );
}

export function SparkList({ conn, projectId, onOpenCard, toast, compact = false }: {
  conn: ProjectConn;
  projectId: string;
  onOpenCard: (cardId: string) => void;
  toast: Toast;
  compact?: boolean;
}) {
  const spark = sparkArray(conn.doc);
  useYVersion(spark);
  const ids = spark.toArray();
  const [over, setOver] = useState<number | null>(null);
  return (
    <section className={compact ? 'spark compact' : 'spark'} aria-label="Spark">
      <h2>Spark</h2>
      <SparkAdd conn={conn} projectId={projectId} toast={toast} />
      {ids.length === 0 && <p className="muted">Nothing new. Ideas from your phone land here.</p>}
      <ol>
        {ids.map((id, i) => (
          <li
            key={id}
            className={over === i ? 'drop-before' : ''}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(CARD_DRAG_TYPE, id);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(e) => {
              if (e.dataTransfer.types.includes(CARD_DRAG_TYPE)) {
                e.preventDefault();
                setOver(i);
              }
            }}
            onDragLeave={() => setOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOver(null);
              const dragged = e.dataTransfer.getData(CARD_DRAG_TYPE);
              if (dragged && dragged !== id) moveSpark(conn.doc, dragged, i);
            }}
            data-card-id={id}
          >
            <CardBody conn={conn} projectId={projectId} cardId={id} editing={false} onDoneEditing={() => {}} />
            <button className="open-card" aria-label="Open card" onClick={() => onOpenCard(id)}>⤢</button>
          </li>
        ))}
      </ol>
    </section>
  );
}
